import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { orpc } from '../../../apis/orpc-query';
import { useAuth } from '../../../auth';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { StatCard } from '../../../components/ui/DefinitionList';
import { Spinner } from '../../../components/ui/Spinner';
import { StatusText } from '../../../components/ui/StatusText';
import { Tabs } from '../../../components/ui/Tabs';
import { formatDay, formatDays, formatMoney } from '../../../lib/format';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { inventoryStatusTone, isVehicleTab, VEHICLE_TABS, type VehicleTab, vehicleTitle } from '../vehicles-display';
import { CorrectionDialog } from './CorrectionDialog';
import { StatusDialog } from './StatusDialog';
import { VehicleActivityTab } from './VehicleActivityTab';
import { VehicleCostsTab } from './VehicleCostsTab';
import { VehicleDocumentsTab } from './VehicleDocumentsTab';
import { VehicleInspectionTab } from './VehicleInspectionTab';
import { VehicleOverviewTab } from './VehicleOverviewTab';

/**
 * `/vehicles/:id` — the stock vehicle dossier (plan §7).
 *
 * The header is the whole provenance in two lines: what the car is, which stock number it took, when CTA
 * bought it, and **which estimate it came from**. That last badge is the one thing the old lifecycle
 * could not say, and it is why the tabs below open with a condition, a figure and a history rather than
 * with empty forms — the purchase copied all three across (plan §5.6).
 *
 * The header's actions follow the car. While it is in stock everybody gets "Update inventory status"
 * (warehouse ⇄ workshop, or the sale); once it is sold there is nothing left to move and that button
 * goes, which is what "SOLD is closed" means in a screen rather than in a rule. "Correct…" is an
 * administrator's and is offered on **any** vehicle: the purchase price and date of an open car are
 * recorded by the purchase and have no ordinary edit of their own, so a confirmed price typed wrong was
 * otherwise uncorrectable until the car was sold, with every figure derived from it wrong in the
 * meantime (plan §5.6; the dialog itself restricts which fields an open vehicle offers).
 */
export const VehicleDetailPage = () => {
  const { t } = useI18nTranslation();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isStatusOpen, setStatusOpen] = useState(false);
  const [isCorrectionOpen, setCorrectionOpen] = useState(false);

  const detailQuery = useQuery(orpc.vehicles.get.queryOptions({ input: { vehicleId: id ?? '' }, enabled: Boolean(id) }));
  const vehicle = detailQuery.data ?? null;

  const rawTab = searchParams.get('tab');
  const tab: VehicleTab = isVehicleTab(rawTab) ? rawTab : 'OVERVIEW';

  const onChanged = useCallback(async (): Promise<void> => {
    await Promise.all([
      detailQuery.refetch(),
      queryClient.invalidateQueries({ queryKey: orpc.vehicles.list.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.vehicles.listActivity.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.inspections.get.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.documents.list.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.valuations.list.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.dashboard.stats.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.dashboard.queues.key() }),
    ]);
  }, [detailQuery, queryClient]);

  if (detailQuery.isError) {
    return (
      <section className="rounded-lg border border-destructive/40 bg-destructive/5 p-6">
        <h1 className="text-base font-semibold text-destructive">{t('vehicles.loadFailedTitle')}</h1>
        <p className="mt-2 text-sm text-destructive">{t('vehicles.loadFailedBody')}</p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            isLoading={detailQuery.isFetching}
            onClick={() => void detailQuery.refetch()}
          >
            {t('actions.retry')}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/vehicles')}
          >
            {t('vehicles.backToList')}
          </Button>
        </div>
      </section>
    );
  }

  if (detailQuery.isLoading || vehicle === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner />
        {t('app.loading')}
      </div>
    );
  }

  const isSold = vehicle.status === 'SOLD';
  const editable = !isSold;

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/vehicles')}
        >
          <ArrowLeft className="size-3.5 rtl:rotate-180" />
          {t('vehicles.backToList')}
        </Button>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-foreground">{vehicleTitle(vehicle) === '' ? vehicle.stockNo : vehicleTitle(vehicle)}</h1>
            <p className="mt-1 font-mono text-sm text-muted-foreground">
              {vehicle.stockNo} · {t('vehicles.purchasedOn', { date: formatDay(vehicle.purchaseDate) })}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <StatusText
              label={t(`enums.inventoryStatus.${vehicle.status}`)}
              tone={inventoryStatusTone(vehicle.status)}
            />
            {isSold ? null : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStatusOpen(true)}
              >
                {t('vehicles.updateStatus')}
              </Button>
            )}
            {isAdmin ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCorrectionOpen(true)}
              >
                {t('vehicles.correct')}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {vehicle.sourceEstimate ? (
            <Link to={`/estimates/${vehicle.sourceEstimate.id}`}>
              <Badge mono>
                {t('vehicles.fromEstimate', { estimateNo: vehicle.sourceEstimate.estimateNo })}
                {vehicle.template ? ` · ${vehicle.template.name} v${vehicle.template.version}` : ''}
              </Badge>
            </Link>
          ) : (
            <Badge>{t('vehicles.noSourceEstimate')}</Badge>
          )}
          {vehicle.policy ? <Badge mono>{t('estimates.equationsVersion', { version: vehicle.policy.version })}</Badge> : null}
          {vehicle.latestSnapshot?.isStale ? <Badge tone="warning">{t('estimates.snapshotStale')}</Badge> : null}
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t('vehicles.purchasePrice')}
          value={<Money value={vehicle.purchasePrice} />}
          hint={formatDay(vehicle.purchaseDate)}
        />
        <StatCard
          label={t('vehicles.askingPrice')}
          value={<Money value={vehicle.askingPrice} />}
          hint={t('vehicles.askingPriceHint')}
        />
        <StatCard
          label={t('estimates.requiredRetail')}
          value={<Money value={vehicle.valuation.outputs.retail ?? null} />}
          hint={t('vehicles.requiredRetailHint')}
        />
        {isSold ? (
          <StatCard
            label={t('vehicles.salePrice')}
            value={<Money value={vehicle.salePrice} />}
            hint={t('vehicles.soldInDays', { date: formatDay(vehicle.saleDate), days: formatDays(vehicle.daysInStock) })}
          />
        ) : (
          <StatCard
            label={t('vehicles.daysInStock')}
            value={
              <span
                dir="ltr"
                className="tabular-nums"
              >
                {formatDays(vehicle.daysInStock)}
              </span>
            }
            hint={t('vehicles.notSold')}
          />
        )}
      </div>

      <Tabs
        items={VEHICLE_TABS.map((value) => ({
          value,
          label: t(`vehicles.tabs.${value}`),
          ...(value === 'ACTIVITY' ? { badge: vehicle.activityCount } : {}),
          ...(value === 'COSTS' ? { badge: vehicle.costItems.count } : {}),
          ...(value === 'DOCUMENTS' ? { badge: vehicle.documents.count } : {}),
        }))}
        value={tab}
        onChange={(next) => setSearchParams({ tab: next }, { replace: true })}
      />

      {tab === 'OVERVIEW' ? (
        <VehicleOverviewTab
          vehicle={vehicle}
          editable={editable}
          onChanged={onChanged}
        />
      ) : tab === 'INSPECTION' ? (
        <VehicleInspectionTab
          vehicle={vehicle}
          editable={editable}
          onChanged={onChanged}
        />
      ) : tab === 'COSTS' ? (
        <VehicleCostsTab
          vehicle={vehicle}
          editable={editable}
          onChanged={onChanged}
        />
      ) : tab === 'DOCUMENTS' ? (
        /* The one tab a SOLD vehicle keeps: an invoice for work done last month arrives after the car
           has gone, and refusing it would leave the paperwork in somebody's inbox (plan §5.6). */
        <VehicleDocumentsTab
          vehicle={vehicle}
          onChanged={onChanged}
        />
      ) : (
        <VehicleActivityTab vehicleId={vehicle.id} />
      )}

      <StatusDialog
        vehicle={vehicle}
        isOpen={isStatusOpen}
        onClose={() => setStatusOpen(false)}
        onChanged={onChanged}
      />

      <CorrectionDialog
        vehicle={vehicle}
        isOpen={isCorrectionOpen}
        onClose={() => setCorrectionOpen(false)}
        onChanged={onChanged}
      />
    </div>
  );
};

const Money = ({ value }: { value: number | null | undefined }) => (
  <span
    dir="ltr"
    className="tabular-nums"
  >
    {formatMoney(value)}
  </span>
);
